import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Hexagon, ShieldCheck, Zap, Coins, ArrowRight, ChevronRight, Globe, Lock, Download, Chrome, Package, Swords, Gift, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Link Shield',
    desc: 'Automatically detect and protect creator affiliate links from being hijacked by discount extensions.'
  },
  {
    icon: Swords,
    title: 'Complete Quests',
    desc: 'Take on challenges — share screenshots, visit links, submit receipts — and earn bonus points for each one.'
  },
  {
    icon: Gift,
    title: 'Redeem Rewards',
    desc: 'Cash in your points for Amazon, Apple, and Uber Eats gift cards, Fortnite V-Bucks, or direct cashback.'
  },
  {
    icon: Crown,
    title: 'Creator Dashboard',
    desc: 'Creators get analytics on their protected links, top supporters, and can manage their own discount codes.'
  },
  {
    icon: Globe,
    title: 'Works Everywhere',
    desc: 'Compatible with Amazon, ShareASale, CJ Affiliate, Rakuten, and hundreds more affiliate networks.'
  },
  {
    icon: Lock,
    title: 'Privacy First',
    desc: 'No tracking, no data selling. We only protect links — nothing else leaves your browser.'
  }
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090B] overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Hexagon className="w-8 h-8 text-amber-500 fill-amber-500/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2 h-2 bg-amber-500 rounded-full" />
              </div>
            </div>
            <span className="font-heading font-black text-xl tracking-tight">
              <span className="text-white">Honey</span>
              <span className="text-amber-500">Pot</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="landing-login-btn"
              variant="ghost"
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate('/login')}
            >
              Log in
            </Button>
            <button
              data-testid="landing-signup-btn"
              className="honey-btn px-5 py-2 rounded-full text-sm"
              onClick={() => navigate('/signup')}
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="absolute inset-0 honeycomb-bg opacity-40" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[120px]" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8">
              <Zap className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-amber-400">Protecting the Creator Economy</span>
            </div>
            
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6">
              Stop affiliate link
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                hijacking
              </span>
              , forever.
            </h1>
            
            <p className="text-base lg:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Other extensions secretly swap creator affiliate links to pocket commissions for themselves. 
              Honey Pot keeps the original link intact — so creators always get credited.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                data-testid="hero-cta-btn"
                className="honey-btn px-8 py-3.5 rounded-full text-base flex items-center gap-2"
                onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Download className="w-4 h-4" />
                Install Honey Pot
              </button>
            </div>
          </motion.div>

          {/* Extension Preview */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 max-w-sm mx-auto"
          >
            <div className="glass-card rounded-2xl p-6 amber-glow">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-black" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-white">Link Protected</p>
                  <p className="text-xs text-zinc-500">amazon.com/dp/B0C...</p>
                </div>
                <div className="ml-auto px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <span className="text-[10px] font-bold text-emerald-400">+1 PT</span>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-500 border-t border-white/5 pt-4">
                <span>Creator: TechReviewPro</span>
                <span className="text-amber-500 font-medium">Amazon Associates</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white mb-4">
              Protect, earn, and redeem
            </h2>
            <p className="text-base text-zinc-500 max-w-lg mx-auto">
              Browse normally. Honey Pot protects creator links, rewards you with points, and lets you cash in.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-8 group hover:border-amber-500/20 transition-all duration-300"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-5 group-hover:bg-amber-500/20 transition-colors">
                  <f.icon className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Extension Download */}
      <section className="py-24 px-6 relative" id="download">
        <div className="absolute inset-0 bg-gradient-to-t from-amber-500/5 to-transparent" />
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
              <Chrome className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-amber-400">Chrome Extension Available</span>
            </div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white mb-4">
              Install the Extension
            </h2>
            <p className="text-base text-zinc-400 max-w-lg mx-auto">
              Download and install Honey Pot directly in Chrome. Protect creator links on every page you visit.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 amber-glow"
          >
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0">
                <Package className="w-8 h-8 text-black" />
              </div>
              <div className="text-center sm:text-left flex-1">
                <h3 className="font-heading font-bold text-lg text-white mb-1">Honey Pot for Chrome</h3>
                <p className="text-sm text-zinc-400">Version 1.3.0 &middot; Works on Chromium-based browsers</p>
              </div>
              <a
                href={`${API}/api/extension/download`}
                data-testid="download-extension-btn"
                className="honey-btn px-6 py-3 rounded-xl text-sm font-bold flex items-center gap-2 flex-shrink-0"
                download
              >
                <Download className="w-4 h-4" />
                Download .zip
              </a>
            </div>

            <div className="border-t border-white/5 pt-6">
              <p className="text-xs font-bold text-zinc-400 mb-4 uppercase tracking-wider">How to Install</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { step: "1", title: "Download & Unzip", desc: "Click the button above, then extract the zip file." },
                  { step: "2", title: "Open Extensions", desc: "Go to chrome://extensions and enable Developer Mode." },
                  { step: "3", title: "Load Extension", desc: "Click 'Load unpacked' and select the extracted folder." }
                ].map((s, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-amber-500">{s.step}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{s.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 relative">
        <div className="relative max-w-2xl mx-auto text-center">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-white mb-4">
            Ready to protect creators?
          </h2>
          <p className="text-base text-zinc-400 mb-8">
            Join thousands of users who browse ethically.
          </p>
          <button
            data-testid="cta-signup-btn"
            className="honey-btn px-10 py-4 rounded-full text-base flex items-center gap-2 mx-auto"
            onClick={() => navigate('/signup')}
          >
            Get Started Free
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hexagon className="w-5 h-5 text-amber-500 fill-amber-500/20" />
            <span className="font-heading font-bold text-sm text-zinc-500">HoneyPot</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/privacy')} className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="footer-privacy-link">Privacy Policy</button>
            <p className="text-xs text-zinc-600">Protecting the creator economy, one link at a time.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
